import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import CompactQuoteForm from "@/components/forms/CompactQuoteForm";
import { FINAL_EXPENSE_USES, PRODUCT_DISCLOSURE } from "@/data/siteContent";

const SECTIONS = [
  {
    title: "How Final Expense Whole Life Generally Works",
    body: [
      "You choose a coverage amount and a beneficiary, answer the carrier's health questions, and pay a premium — usually monthly. In return, the policy promises to pay a death benefit to your beneficiary when you pass away, as long as the policy is in force.",
      "Because it is whole life insurance, coverage is designed to last your entire life rather than expiring after a set number of years. Premiums on many products are level, meaning they are designed to stay the same, and some policies build a small cash value over time.",
    ],
  },
  {
    title: "Level Benefit vs. Graded or Modified Benefit",
    body: [
      "A level benefit policy pays the full death benefit from the first day of coverage (subject to policy terms). These are generally available to applicants who can answer the health questions favorably.",
      "A graded or modified benefit policy is often available to applicants with more significant health history. If death occurs during the first two or three years, these policies typically pay a reduced benefit — often a return of the premiums paid plus interest — and pay the full benefit after that initial period. The exact terms vary by carrier and policy.",
    ],
  },
  {
    title: "Simplified Underwriting and Medical Questions",
    body: [
      "Most final expense policies use simplified underwriting: a short set of yes-or-no health questions instead of a full medical exam. Many applicants complete an application by phone in one sitting. Some carriers also check prescription history databases as part of their review.",
      "Whether an exam is required depends on the carrier, the product, the coverage amount, and your answers. Michael will tell you what to expect before you apply.",
    ],
  },
  {
    title: "Why Truthful Answers Matter",
    body: [
      "An insurance application is a legal document. Answering health questions completely and honestly protects your family. During the contestability period — typically the first two years of a policy — an insurer can investigate a claim and may deny it if the application contained material misstatements. Policies also generally include a suicide provision during a similar initial period. These provisions vary by policy and state.",
    ],
  },
  {
    title: "Beneficiaries, Premiums, and Keeping Coverage in Force",
    body: [
      "You choose who receives the benefit — often a spouse, child, or other loved one — and you can usually change beneficiaries later. Premiums are most often paid monthly by bank draft, with other schedules available depending on the carrier.",
      "If premiums stop, a policy can lapse and coverage ends. Some policies with cash value offer options in that situation. Before choosing a policy, ask what happens if a payment is missed and what the grace period is.",
    ],
  },
  {
    title: "Questions Worth Asking Before You Choose",
    body: [
      "Is the benefit level from day one, or graded for the first few years? Will my premium ever increase? Can the coverage amount ever decrease? What health questions will be asked? What are the exclusions and limitations? How long does approval usually take? A good agent welcomes every one of these questions — and answers them plainly.",
    ],
  },
];

export default function FinalExpense() {
  return (
    <>
      <SEO
        title="Final Expense Insurance Explained | 1907 Insurance Group"
        description="Learn how final expense whole life insurance works — level vs. graded benefits, simplified underwriting, and what to ask before choosing a policy. Guidance from licensed agent Michael Janas."
      />
      <PageHero
        eyebrow="Final Expense Life Insurance"
        title="A Simple Way to Spare Your Family the Hardest Bill"
        intro="Final expense insurance helps the people you love cover funeral costs, medical bills, and other final obligations — with coverage designed to last your lifetime."
      >
        <Reveal delay={0.25} className="mt-10 max-w-2xl">
          <CompactQuoteForm title="Request a Final Expense Quote" />
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24" data-testid="fe-uses-section">
        <div className="container-site grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl tracking-tight">What the Benefit Can Help Pay For</h2>
            <p className="mt-5 text-lg text-mutedink leading-relaxed">
              Your beneficiary receives the benefit and decides how to use it. Common uses include:
            </p>
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {FINAL_EXPENSE_USES.map((u) => (
                <li key={u} className="flex items-start gap-2.5 text-mutedink">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" /> {u}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1662987619545-1844207dedac?w=1200&q=80&auto=format&fit=crop"
                alt="A multigenerational family spending time together at home"
                className="h-full w-full object-cover"
                loading="lazy"
                data-testid="fe-family-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist py-16 md:py-24" data-testid="fe-education-section">
        <div className="container-site max-w-4xl space-y-14">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title}>
              <h2 className="text-2xl sm:text-3xl tracking-tight" data-testid={`fe-section-title-${i}`}>{s.title}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-4 text-lg text-mutedink leading-relaxed">{p}</p>
              ))}
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="fe-fit-section">
        <div className="container-site grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-navy-100 bg-white p-8 shadow-sm" data-testid="fe-good-fit">
              <h3 className="text-2xl">Final Expense May Be Worth Considering If…</h3>
              <ul className="mt-6 space-y-3.5">
                {[
                  "You want your family to have money available for funeral and final costs",
                  "You prefer permanent coverage over a policy that expires",
                  "You want a predictable premium that fits a fixed income",
                  "You have health concerns and want to know what simplified options exist",
                  "You delayed buying life insurance and want to take care of it now",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-mutedink leading-relaxed">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-gold-dark" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-2xl border border-navy-100 bg-white p-8 shadow-sm" data-testid="fe-not-fit">
              <h3 className="text-2xl">It May Not Be the Best Fit If…</h3>
              <ul className="mt-6 space-y-3.5">
                {[
                  "You need a large amount of income replacement for a working spouse or young children — term life may fit better",
                  "You already have sufficient permanent coverage in force",
                  "You are primarily looking for an investment product — life insurance is protection, not an investment",
                  "Your budget cannot comfortably support an ongoing premium",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-mutedink leading-relaxed">
                    <X className="mt-1 h-5 w-5 shrink-0 text-navy/50" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <p className="container-site mt-10 text-sm text-mutedink leading-relaxed max-w-3xl">{PRODUCT_DISCLOSURE}</p>
      </section>

      <section className="pb-20">
        <div className="container-site max-w-2xl">
          <CompactQuoteForm title="Start Your Final Expense Quote" />
        </div>
      </section>
      <CTASection />
    </>
  );
}
